import React, { useEffect, useState } from "react";
// Chakra imports
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Icon,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  MdOutlineMail,
  MdOutlineEmail,
  MdOutlineMailOutline,
  MdMarkEmailRead,
} from "react-icons/md";
import { 
  FaEnvelope, 
  FaEnvelopeOpen, 
  FaEnvelopeOpenText, 
  FaPaperPlane, 
  FaMailBulk 
} from "react-icons/fa";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { keyframes } from "@emotion/react";
import useAxios from "axios-hooks";
import { useSetRecoilState } from "recoil";
import { toastState } from "atoms/toastState";
import { configureAxios } from "utils/api";

import DefaultAuth from "layouts/auth/Default";
import LOGO from "../../../assets/img/Urzza_Auth_Logo.png";

// Define animations
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const flyRight = keyframes`
  0% { transform: translateX(-20px); opacity: 0; }
  20% { opacity: 1; }
  100% { transform: translateX(20px); opacity: 0; }
`;
const ForgotPasswordPage = () => {
  // Chakra color mode
  //const textColor = useColorModeValue("navy.700", "white");
  const textColor = "white";
  //const textColorSecondary = "gray.400";
  const textColorSecondary = "white";
  const textColorDetails = useColorModeValue("navy.700", "secondaryGray.600");
  const textColorBrand = useColorModeValue("brand.500", "white");
  const brandStars = useColorModeValue("brand.500", "brand.400");
  const googleBg = useColorModeValue("secondaryGray.300", "whiteAlpha.200");
  const googleText = useColorModeValue("navy.700", "white");
  const [isLoading, setIsLoading] = useState(false);
  const googleHover = useColorModeValue(
    { bg: "gray.200" },
    { bg: "whiteAlpha.300" }
  );
  const googleActive = useColorModeValue(
    { bg: "secondaryGray.300" },
    { bg: "whiteAlpha.200" }
  );

  const setToast = useSetRecoilState(toastState);
  const navigate = useNavigate();

  const schema = yup.object().shape({
    Email: yup
      .string()
      .email("Invalid Email Format")
      .required("Email is Required"),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    register,
    setValue,
    getValues,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const [{ data, loading, error }, execute] = useAxios(
    { method: "POST", url: `/api/auth/forgotpassword` },
    { manual: true }
  );

  const submitHandler = async () => {
    setIsLoading(true);
    const formValues = getValues();
    try {
      let reqData = {};
      reqData["email"] = formValues?.Email;
      const response = await execute({ data: reqData });
      navigate("/auth/verifyotp", { state: { email: formValues?.Email } });
      setToast({
        isOpen: true,
        title: `Reset Password link sent to ${formValues?.Email}`,
        status: "success",
      });
    } catch (error) {
      setToast({
        isOpen: true,
        title: "Error sending reset password link",
        status: "error",
      });
    }

    setIsLoading(false);
  };
  useEffect(() => {
    configureAxios(null);
  }, []);
  return (
    <DefaultAuth>
      {/* Split background - dark on left, white on right */}
      <Box
        position="absolute"
        top="0"
        left="0"
        width={{ base: "100%", md: "50%" }}
        height="100%"
        bg="#151c24"
        zIndex="-1"
      />
      <Box
        position="absolute"
        top="0"
        right="0"
        width={{ base: "0%", md: "50%" }}
        height="100%"
        bg="white"
        zIndex="-1"
        display={{ base: "none", md: "block" }}
      />

      {/* Centered glassmorphic card for forgot password form */}
      <Flex
        direction="column"
        align="center"
        justify="center"
        bg="rgba(21, 28, 36, 0.75)"
        borderRadius="2xl"
        boxShadow="0 8px 32px 0 rgba(31, 38, 135, 0.18)"
        backdropFilter="blur(18px)"
        border="1.5px solid rgba(245, 125, 54, 0.15)"
        w={["90%", "85%", "80%", "80%"]}
        maxW="450px"
        p={8}
        position="absolute"
        left={{ base: "50%", md: "25%" }}
        transform="translateX(-50%)"
        zIndex={1}
        _before={{
          content: '""',
          position: "absolute",
          top: "-2px",
          left: "-2px",
          right: "-2px",
          bottom: "-2px",
          borderRadius: "2xl",
          border: "2px solid #f57d36",
          opacity: 0.1,
          zIndex: -1,
          pointerEvents: "none",
        }}
      >
        <Flex direction="column" align="center" justify="center" w="100%">
          <Image 
            src={LOGO} 
            alt="Urzza Logo" 
            width="auto"
            height="80px"
            mb={6} 
            filter="drop-shadow(0 2px 12px rgba(245, 125, 54, 0.5))" 
            objectFit="contain"
            preserveAspectRatio="xMidYMid meet"
          />
          <Heading color={textColor} fontSize="2xl" mb={2} fontWeight="bold" letterSpacing={1.2}>
            Forgot Password
          </Heading>
          <Text color="#f57d36" fontSize="lg" mb={1} fontWeight="semibold" letterSpacing={0.6}>
            Password Recovery
          </Text>
          <Text color="gray.300" fontSize="md" mb={8} textAlign="center">
            Enter your email to receive a password reset link
          </Text>

          <form onSubmit={handleSubmit(submitHandler)} style={{ width: "100%" }}>
            <Controller
              name="Email"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <FormControl isInvalid={errors.Email} mb={6}>
                  <FormLabel color={textColor} fontWeight="semibold">
                    Email
                  </FormLabel>
                  <InputGroup>
                    <Input
                      {...field}
                      type="email"
                      placeholder="Enter your registered email"
                      bg="rgba(255,255,255,0.13)"
                      border="1.5px solid rgba(245, 125, 54, 0.22)"
                      color={textColor}
                      fontSize="lg"
                      py={6}
                      px={5}
                      borderRadius="xl"
                      _focus={{
                        borderColor: "#f57d36",
                        boxShadow: `0 0 0 2px #f57d36`,
                        bg: "rgba(255,255,255,0.22)",
                      }}
                      transition="all 0.18s cubic-bezier(.4,2,.6,1)"
                    />
                    <InputRightElement pointerEvents="none">
                      <Icon as={MdOutlineMail} color="#f57d36" boxSize={6} />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{errors.Email && errors.Email.message}</FormErrorMessage>
                </FormControl>
              )}
            />

            <Flex justify="space-between" align="center" mt={6} mb={8} w="100%">
              <Button
                type="submit"
                w="60%"
                py={6}
                fontSize="lg"
                fontWeight="bold"
                color="white"
                bgGradient="linear(90deg, #f57d36 60%, #ff914d 100%)"
                boxShadow="0 2px 16px 0 rgba(245, 125, 54, 0.18)"
                borderRadius="xl"
                _hover={{
                  bg: "#f57d36",
                  boxShadow: "0 4px 24px 0 rgba(245, 125, 54, 0.29)",
                  transform: "scale(1.03)",
                }}
                _active={{
                  bg: "#f57d36",
                  boxShadow: "0 2px 12px 0 rgba(245, 125, 54, 0.16)",
                }}
                transition="all 0.18s cubic-bezier(.4,2,.6,1)"
                isLoading={isLoading}
                loadingText="Submitting..."
              >
                Reset Password
              </Button>
              <Text
                as="button"
                color="#f57d36"
                fontSize="sm"
                fontWeight="medium"
                bg="none"
                border="none"
                cursor="pointer"
                _hover={{ textDecoration: "underline", color: "#ff914d" }}
                onClick={() => navigate("/auth/signIn")}
                transition="color 0.15s"
                type="button"
              >
                Back to Sign In
              </Text>
            </Flex>
          </form>
        </Flex>
      </Flex>

      {/* Right side illustration with animated icons - hidden on mobile */}
      <Box
        position="absolute"
        top="0"
        right="0"
        width="50%"
        height="100%"
        zIndex={0}
        display={{ base: "none", md: "none", lg: "flex" }}
        alignItems="center"
        justifyContent="center"
        bg="white"
      >
        <Box
          width="80%"
          maxW="600px"
          height="auto"
          bg="white"
          borderRadius="2xl"
          boxShadow="0 8px 32px 0 rgba(31, 38, 135, 0.08)"
          border="1.5px solid rgba(245, 125, 54, 0.15)"
          p={8}
          textAlign="center"
        >
          <Box 
            position="relative"
            h="280px"
            mb={5}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {/* Main envelope icon with pulse animation */}
            <Box
              position="relative"
              animation={`${pulse} 2s infinite ease-in-out`}
              zIndex={2}
            >
              <Icon 
                as={FaEnvelope} 
                boxSize={36} 
                color="#f57d36"
                filter="drop-shadow(0 8px 20px rgba(245, 125, 54, 0.3))"
              />
            </Box>
            
            {/* Flying paper plane animation */}
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              zIndex={1}
            >
              <Box
                animation={`${flyRight} 4s infinite ease-in-out`}
                position="relative"
              >
                <Icon 
                  as={FaPaperPlane} 
                  boxSize={16} 
                  color="#f57d36"
                  opacity={0.7}
                />
              </Box>
            </Box>
            
            {/* Circle of icons around the main icon */}
            <Box position="absolute" top="50%" left="50%" w="240px" h="240px" transform="translate(-50%, -50%)">
              {/* Email icon */}
              <Box position="absolute" top="0" left="50%" transform="translateX(-50%)" animation={`${float} 3s infinite ease-in-out`}>
                <Icon as={MdOutlineEmail} boxSize={10} color="#3182CE" />
              </Box>
              
              {/* Open envelope icon */}
              <Box position="absolute" top="25%" right="0" animation={`${float} 3s infinite ease-in-out 0.5s`}>
                <Icon as={FaEnvelopeOpen} boxSize={10} color="#38A169" />
              </Box>
              
              {/* Email read icon */}
              <Box position="absolute" bottom="0" left="50%" transform="translateX(-50%)" animation={`${float} 3s infinite ease-in-out 1s`}>
                <Icon as={MdMarkEmailRead} boxSize={10} color="#805AD5" />
              </Box>
              
              {/* Mail outline icon */}
              <Box position="absolute" top="25%" left="0" animation={`${float} 3s infinite ease-in-out 1.5s`}>
                <Icon as={MdOutlineMailOutline} boxSize={10} color="#DD6B20" />
              </Box>
              
              {/* Open text envelope icon */}
              <Box position="absolute" bottom="25%" right="0" animation={`${float} 3s infinite ease-in-out 2s`}>
                <Icon as={FaEnvelopeOpenText} boxSize={10} color="#E53E3E" />
              </Box>
              
              {/* Mail bulk icon */}
              <Box position="absolute" bottom="25%" left="0" animation={`${float} 3s infinite ease-in-out 2.5s`}>
                <Icon as={FaMailBulk} boxSize={10} color="#D53F8C" />
              </Box>
            </Box>
          </Box>
          
          <Heading color="#2D3748" fontSize="2xl" mb={3} fontWeight="600">
            Password Recovery
          </Heading>
          <Text color="rgba(45, 55, 72, 0.8)" fontSize="lg">
            We'll help you get back into your account quickly and securely
          </Text>
        </Box>
      </Box>
    </DefaultAuth>
  );
};

export default ForgotPasswordPage;
